
import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center bg-storystore-blue overflow-hidden"
    >
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-storystore-blue/90 to-storystore-blue/80 z-10"></div>
        {/* Background image commented out to fix display issue
        <img 
          src="/lovable-uploads/f9b1378e-ea7e-4025-af88-269665f027a4.png" 
          alt="Background Image" 
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        */}
      </div>

      {/* Animated particles in background */}
      <div className="absolute inset-0 z-5 opacity-20">
        {Array(isMobile ? 5 : 10).fill(0).map((_, i) => (
          <div 
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              width: Math.random() * 6 + 2 + 'px', 
              height: Math.random() * 6 + 2 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animation: `float ${Math.random() * 10 + 20}s linear infinite`,
              animationDelay: `${Math.random() * 10}s`,
              opacity: Math.random() * 0.5 + 0.3
            }}
          />
        ))}
      </div>

      {/* Logo animation in background */}
      <div className={`absolute right-0 bottom-0 opacity-20 md:opacity-30 transform rotate-12 animate-pulse ${isMobile ? 'scale-75' : ''}`}>
        <img 
          src="/lovable-uploads/a416c1a4-be00-4547-a393-fd31cb9ca1a1.png" 
          alt="Story Store Logo Animation" 
          className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl"
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 py-0 sm:py-12 z-10 relative">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 backdrop-blur-sm transform translate-y-0 opacity-0 animate-[fadeSlideUp_0.6s_0.2s_forwards]">
            <span className="text-storystore-red">✦</span> Kreativ mediebyrå i Stockholm
          </div>
          
          <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Förverkligar din <span className="text-storystore-red relative inline-block">
              berättelse
              <span className="absolute -bottom-1 left-0 w-full h-1 bg-storystore-red transform scale-x-100 animate-pulse"></span>
            </span>
          </h1>
          
          <p className={`text-lg sm:text-xl md:text-2xl text-white/90 mb-6 sm:mb-10 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Vi är en kreativ mediebyrå som hjälper företag och influencers att 
            synas genom reklam, content och filmproduktion med <span className="font-semibold">konkreta resultat</span>.
          </p>
          
          <div className={`flex flex-wrap gap-3 sm:gap-4 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <a href="#tjanster" className="btn-primary text-sm sm:text-base inline-flex items-center gap-1 sm:gap-2 relative overflow-hidden group py-2.5 px-4 sm:py-3 sm:px-6">
              <span className="relative z-10">Utforska våra tjänster</span>
              <ArrowRight size={isMobile ? 16 : 18} className="relative z-10 group-hover:translate-x-1 transition-transform" />
              <div className="absolute top-0 left-0 w-full h-full bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            </a>
            <a href="#kontakt" className="btn-outline bg-white/10 text-white border-white hover:bg-white hover:text-storystore-blue transition-all duration-300 group text-sm sm:text-base py-2.5 px-4 sm:py-3 sm:px-6">
              <span className="group-hover:translate-x-1 transition-transform inline-block">Kontakta oss</span>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll down indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center text-white animate-pulse">
        <span className="text-xs sm:text-sm mb-1 sm:mb-2 opacity-80">Scrolla för mer</span>
        <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white rounded-full flex items-start justify-center p-1">
          <div className="w-1 h-2 bg-white rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
